import { cn } from '@skupreme/shared/ui';
import { createElement, forwardRef, type HTMLAttributes } from 'react';

/*
 * Plain `.ts`, so these are built with createElement. Each forwards className,
 * since screens extend them with `styled(GreyText)` and the like.
 */

type SpanProps = HTMLAttributes<HTMLSpanElement>;
type DivProps = HTMLAttributes<HTMLDivElement>;

function span(displayName: string, classes: string) {
  const Text = forwardRef<HTMLSpanElement, SpanProps>(
    ({ className, ...rest }, ref) =>
      createElement('span', {
        ...rest,
        ref,
        className: cn(classes, className),
      }),
  );
  Text.displayName = displayName;
  return Text;
}

function div(displayName: string, classes: string) {
  const Block = forwardRef<HTMLDivElement, DivProps>(
    ({ className, ...rest }, ref) =>
      createElement('div', { ...rest, ref, className: cn(classes, className) }),
  );
  Block.displayName = displayName;
  return Block;
}

export const Row = div(
  'Row',
  'sk:flex sk:items-center sk:justify-between sk:border-b sk:border-rule sk:p-4 sk:text-fg-secondary',
);

export const Container = div(
  'Container',
  'sk:m-0 sk:h-full sk:rounded-card sk:bg-surface sk:p-0 sk:shadow-card',
);

export const NumberCell = forwardRef<
  HTMLSpanElement,
  SpanProps & { fontSize?: string; up?: boolean; down?: boolean }
>(({ fontSize, up, down, className, style, ...rest }, ref) =>
  createElement('span', {
    ...rest,
    ref,
    style: fontSize ? { fontSize, ...style } : style,
    className: cn(
      'sk:text-[17px] sk:font-bold',
      up ? 'sk:text-success' : down ? 'sk:text-danger' : 'sk:text-fg',
      className,
    ),
  }),
);
NumberCell.displayName = 'NumberCell';

export const Title = forwardRef<
  HTMLSpanElement,
  SpanProps & { fontSize?: string; danger?: string; level?: number }
>(({ fontSize, danger, level: _level, className, style, ...rest }, ref) =>
  createElement('span', {
    ...rest,
    ref,
    style: fontSize ? { fontSize, ...style } : style,
    className: cn(
      'sk:text-[25px] sk:leading-[30px] sk:font-bold sk:[&_.rotate-180]:rotate-180',
      danger ? 'sk:text-danger' : 'sk:text-fg',
      className,
    ),
  }),
);
Title.displayName = 'Title';

export const SubTitle = span(
  'SubTitle',
  'sk:text-lg sk:leading-5 sk:text-fg-tertiary',
);

const GREY_TEXT = 'sk:text-[0.8rem] sk:leading-4 sk:text-fg-tertiary';

export const GreyText = span('GreyText', GREY_TEXT);

export const BoldGreyText = span('BoldGreyText', cn(GREY_TEXT, 'sk:text-sm'));

export const ResponsiveDotts = div(
  'ResponsiveDotts',
  'sk:w-full sk:overflow-hidden sk:text-ellipsis sk:font-normal sk:text-fg',
);

export const SmallGreyText = span(
  'SmallGreyText',
  cn(GREY_TEXT, 'sk:text-[0.7rem] sk:leading-[0.8rem]'),
);

/* Below 1670px the number and its chart stack and centre. */
export const NumberContainer = div(
  'NumberContainer',
  cn(
    'sk:flex sk:items-center sk:justify-between sk:px-4 sk:py-2 sk:[&_div]:my-2',
    'sk:max-[1670px]:flex-col sk:max-[1670px]:justify-center sk:max-[1670px]:text-center',
    'sk:max-[1670px]:[&_.global-title]:justify-center!',
    'sk:max-[1670px]:[&>div:nth-child(2)]:pt-3',
  ),
);

export const CardCompactTitle = div(
  'CardCompactTitle',
  'sk:mb-[5px] sk:text-[13px] sk:leading-[15px] sk:font-normal sk:text-fg-tertiary',
);
